import { ref, readonly } from 'vue';
import { getAccessToken, logout } from '@/helpers/utils';

type SessionState = 'active' | 'warning' | 'expired';

const PUBLIC_PATHS = [
  '/',
  '/login',
  '/app/login',
  '/registration',
  '/registration/verification',
  '/registration/verification/liveness',
  '/forgot-password',
  '/success-verification-confirmed',
];

const INACTIVITY_DURATION = 4 * 60 * 60 * 1000; // 4 hours
const COUNTDOWN_SECONDS = 60;

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keypress', 'scroll', 'touchstart'] as const;

// --- Singleton state (module-level, shared across all consumers) ---
const state = ref<SessionState>('active');
const countdown = ref(COUNTDOWN_SECONDS);

let inactivityTimer: ReturnType<typeof setTimeout> | null = null;
let countdownInterval: ReturnType<typeof setInterval> | null = null;
let lastActivity = 0;
let listening = false;

function isPublicPath(path: string): boolean {
  return PUBLIC_PATHS.some((p) => path === p || path.startsWith(p + '/'));
}

function clearTimers() {
  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
    inactivityTimer = null;
  }
  if (countdownInterval) {
    clearInterval(countdownInterval);
    countdownInterval = null;
  }
}

function onActivity() {
  const now = Date.now();
  if (now - lastActivity < 1000) return; // 1s throttle
  lastActivity = now;

  if (state.value === 'active') {
    // Reset the inactivity timer
    if (inactivityTimer) clearTimeout(inactivityTimer);
    inactivityTimer = setTimeout(enterWarning, INACTIVITY_DURATION);
  }
  // Warning state: only explicit "Stay Logged In" button dismisses
}

function addListeners() {
  if (listening) return;
  ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, onActivity, { passive: true }));
  listening = true;
}

function removeListeners() {
  ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, onActivity));
  listening = false;
}

function enterWarning() {
  state.value = 'warning';
  countdown.value = COUNTDOWN_SECONDS;

  countdownInterval = setInterval(() => {
    countdown.value--;
    if (countdown.value <= 0) {
      enterExpired();
    }
  }, 1000);
}

function enterExpired() {
  clearTimers();
  removeListeners();
  state.value = 'expired';
  countdown.value = 0;
  logout();
  window.location.href = '/login';
}

function stayLoggedIn() {
  clearTimers();
  state.value = 'active';
  countdown.value = COUNTDOWN_SECONDS;
  inactivityTimer = setTimeout(enterWarning, INACTIVITY_DURATION);
}

function goToLogin() {
  window.location.href = '/login';
}

function start(path?: string) {
  if (path && isPublicPath(path)) {
    stop();
    return;
  }
  if (!getAccessToken()) {
    stop();
    return;
  }

  // Already running — just reset
  clearTimers();
  state.value = 'active';
  countdown.value = COUNTDOWN_SECONDS;
  lastActivity = Date.now();
  addListeners();
  inactivityTimer = setTimeout(enterWarning, INACTIVITY_DURATION);
}

function stop() {
  clearTimers();
  removeListeners();
  state.value = 'active';
  countdown.value = COUNTDOWN_SECONDS;
}

export function useSessionTimeout() {
  return {
    state: readonly(state),
    countdown: readonly(countdown),
    start,
    stop,
    stayLoggedIn,
    goToLogin,
  };
}
